"use client"

/**
 * Popover: the shared shell for ENRICHED popups (docs/THEME.md 2.10; std-overlays foundation). The content is the
 * caller's (rich rows, previews, live data, small forms); the shell is shared: the popup surface, the layer owner, the
 * custom positioner (PopupPortal: flip, shift, cap, never clipped), and dismissal through the layer stack (Escape and
 * an outside press close the TOP layer only; focus returns to the trigger after Escape or a choice, not after an
 * outside press). Simple choice lists use Select / DropdownMenu look="standard", not this.
 *
 *   <Popover>
 *     <PopoverTrigger asChild><Button variant="outline">Invite</Button></PopoverTrigger>
 *     <PopoverContent aria-label="Invite someone" width="lg">...</PopoverContent>
 *   </Popover>
 */
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"

import { usePopupLayer } from "@/lib/layers/LayerOwner"
import { getTabbableElements } from "@/lib/a11y/tabbable"
import { pushLayer, shouldRestoreFocus, type CloseReason } from "@/lib/layers/layerStack"
import { cn } from "@/lib/utils"
import { PopupPortal } from "./PopupPortal"
import { overlay, type OverlaySize } from "./overlay-recipes"
import { SELECT_LIST_LEGACY_Z } from "./select"

interface PopoverContextValue {
  open: boolean
  setOpen: (open: boolean, reason?: CloseReason) => void
  triggerRef: React.MutableRefObject<HTMLElement | null>
  contentId: string
}

const PopoverContext = React.createContext<PopoverContextValue | null>(null)

function usePopover(part: string): PopoverContextValue {
  const ctx = React.useContext(PopoverContext)
  if (!ctx) throw new Error(`<${part}> must be inside <Popover>`)
  return ctx
}

export interface PopoverProps {
  open?: boolean
  defaultOpen?: boolean
  /** Called with the new state and, on close, why it closed. */
  onOpenChange?: (open: boolean, reason?: CloseReason) => void
  children: React.ReactNode
}

function Popover({ open: openProp, defaultOpen = false, onOpenChange, children }: PopoverProps) {
  const [uncontrolled, setUncontrolled] = React.useState(defaultOpen)
  const open = openProp ?? uncontrolled
  const triggerRef = React.useRef<HTMLElement | null>(null)
  const contentId = React.useId()
  const setOpen = React.useCallback(
    (next: boolean, reason?: CloseReason) => {
      if (openProp === undefined) setUncontrolled(next)
      onOpenChange?.(next, reason)
      if (!next && reason && shouldRestoreFocus(reason)) triggerRef.current?.focus()
    },
    [openProp, onOpenChange],
  )
  const value = React.useMemo(() => ({ open, setOpen, triggerRef, contentId }), [open, setOpen, contentId])
  return <PopoverContext.Provider value={value}>{children}</PopoverContext.Provider>
}

type TriggerProps = React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }

const PopoverTrigger = React.forwardRef<HTMLButtonElement, TriggerProps>(({ asChild, onClick, ...props }, ref) => {
  const { open, setOpen, triggerRef, contentId } = usePopover("PopoverTrigger")
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      ref={(node: HTMLButtonElement | null) => {
        triggerRef.current = node
        if (typeof ref === "function") ref(node)
        else if (ref) ref.current = node
      }}
      type={asChild ? undefined : "button"}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? contentId : undefined}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event)
        if (!event.defaultPrevented) setOpen(!open, open ? "trigger-toggle" : undefined)
      }}
      {...props}
    />
  )
})
PopoverTrigger.displayName = "PopoverTrigger"

export interface PopoverContentProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "role"> {
  side?: "top" | "bottom"
  align?: "start" | "end"
  /** sm 220 / md 280 / lg 340px, or "trigger": at least the trigger's width. Default md. */
  width?: OverlaySize | "trigger"
  /** Move focus into the popover when it opens (default true: it is a non-modal dialog). */
  initialFocus?: boolean
  /** The ARIA role of the panel. Default "dialog" (non-modal). A listbox passes "listbox" or "presentation". */
  role?: string
}

const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  ({ side = "bottom", align = "start", width = "md", initialFocus = true, role = "dialog", className, children, onKeyDown, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, ...props }, ref) => {
    const { open, setOpen, triggerRef, contentId } = usePopover("PopoverContent")
    // Without a layer owner, use the same body-portal band as Select and DropdownMenu.
    const layer = usePopupLayer(SELECT_LIST_LEGACY_Z)
    const panelRef = React.useRef<HTMLDivElement | null>(null)

    React.useEffect(() => {
      if (!open) return
      return pushLayer({
        id: contentId,
        modal: false,
        get trigger() {
          return triggerRef.current
        },
        get content() {
          return panelRef.current
        },
        close: (reason) => setOpen(false, reason),
      })
    }, [open, contentId, setOpen, triggerRef])

    // Mounted only while open: enriched content (live data, previews) does no work while closed.
    if (!open) return null
    return (
      <PopupPortal
        anchorRef={triggerRef}
        panelRef={{
          get current() {
            return panelRef.current
          },
          set current(node: HTMLDivElement | null) {
            panelRef.current = node
            if (typeof ref === "function") ref(node)
            else if (ref) ref.current = node
          },
        }}
        open={open}
        prefer={side}
        align={align}
        gap={4}
        role={role}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        surface="standard"
        matchAnchorWidth={width === "trigger"}
        initialFocus={initialFocus}
        className={cn(layer.zClass, width === "trigger" ? undefined : overlay.popupWidth[width], className)}
      >
        <div
          id={contentId}
          data-popover-content=""
          onKeyDown={(event) => {
            onKeyDown?.(event)
            if (event.defaultPrevented || event.key !== "Tab") return
            // Non-modal: Tab past either end closes it and lands back on the trigger (2.10 keyboard traversal).
            const items = getTabbableElements(event.currentTarget)
            const first = items[0]
            const last = items[items.length - 1]
            const leaving = items.length === 0 || (event.shiftKey ? document.activeElement === first : document.activeElement === last)
            if (leaving) {
              event.preventDefault()
              setOpen(false, "explicit")
            }
          }}
          {...props}
        >
          {children}
        </div>
      </PopupPortal>
    )
  },
)
PopoverContent.displayName = "PopoverContent"

/** Closes the popover (a "Done" button inside enriched content). Focus returns to the trigger. */
const PopoverClose = React.forwardRef<HTMLButtonElement, TriggerProps>(({ asChild, onClick, ...props }, ref) => {
  const { setOpen } = usePopover("PopoverClose")
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : "button"}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event)
        if (!event.defaultPrevented) setOpen(false, "explicit")
      }}
      {...props}
    />
  )
})
PopoverClose.displayName = "PopoverClose"

export { Popover, PopoverTrigger, PopoverContent, PopoverClose, usePopover }
